import * as React from 'react';
import { v4 as uuidV4 } from 'uuid';

import { fetchFromOldApi } from '~/services/Http';

import type {
  ChatMessage,
  ChatThread,
  StreamChatRequest,
  StreamChatResponseMeta,
} from './types';

// Stream parsing
const SUMMARY_START = '[[SUMMARY_START]]';
const SUMMARY_END = '[[SUMMARY_END]]';
const TRACE_START = '[[TRACE_EVENT_START]]';
const TRACE_END = '[[TRACE_EVENT_END]]';

type StreamExtraction = {
  cleanTextDelta: string;
  summary?: string;
  traceId?: string;
};

const createStreamExtractor = () => {
  let buffer = '';
  let summaryBuffer = '';
  let traceBuffer = '';
  let inSummary = false;
  let inTrace = false;

  const push = (chunkText: string): StreamExtraction => {
    buffer += chunkText;

    let visible = '';
    let summaryOut: string | undefined;
    let traceOut: string | undefined;

    while (buffer.length) {
      if (!inSummary && !inTrace) {
        const nextSummary = buffer.indexOf(SUMMARY_START);
        const nextTrace = buffer.indexOf(TRACE_START);

        const tagPos = [nextSummary, nextTrace]
          .filter((i) => i >= 0)
          .sort((a, b) => a - b)[0];

        if (tagPos === undefined) {
          visible += buffer;
          buffer = '';
          break;
        }

        visible += buffer.slice(0, tagPos);
        buffer = buffer.slice(tagPos);

        if (buffer.startsWith(SUMMARY_START)) {
          inSummary = true;
          buffer = buffer.slice(SUMMARY_START.length);
          continue;
        }
        if (buffer.startsWith(TRACE_START)) {
          inTrace = true;
          buffer = buffer.slice(TRACE_START.length);
          continue;
        }
      }

      if (inSummary) {
        const end = buffer.indexOf(SUMMARY_END);
        if (end === -1) {
          summaryBuffer += buffer;
          buffer = '';
          break;
        }
        summaryBuffer += buffer.slice(0, end);
        buffer = buffer.slice(end + SUMMARY_END.length);
        inSummary = false;
        summaryOut = summaryBuffer.trim();
        continue;
      }

      if (inTrace) {
        const end = buffer.indexOf(TRACE_END);
        if (end === -1) {
          traceBuffer += buffer;
          buffer = '';
          break;
        }
        traceBuffer += buffer.slice(0, end);
        buffer = buffer.slice(end + TRACE_END.length);
        inTrace = false;
        traceOut = traceBuffer.trim();
        continue;
      }
    }

    return { cleanTextDelta: visible, summary: summaryOut, traceId: traceOut };
  };

  return { push };
};

const streamChat = async ({
  request,
  onDelta,
  onMeta,
  signal,
}: {
  request: StreamChatRequest;
  onDelta: (textDelta: string) => void;
  onMeta: (meta: StreamChatResponseMeta) => void;
  signal?: AbortSignal;
}) => {
  const res = await fetchFromOldApi('/json/ai/chat/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream, text/plain, application/json',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Chat failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  const extractor = createStreamExtractor();

  // while (true)
  // the loop keeps listening to the server while it is sending the reply,
  // reading each small chunk of text as it arrives, and then as soon as the server finishes
  // (or the chat is closed), the loop stops and no more text is added.
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;

    const text = decoder.decode(value, { stream: true });
    const { cleanTextDelta, summary, traceId } = extractor.push(text);

    if (cleanTextDelta) onDelta(cleanTextDelta);
    if (summary || traceId) onMeta({ summary, traceId });
  }
};

type PersistedState = {
  activeThreadId: string | null;
  threads: Record<string, ChatThread>;
};

const safeJsonParse = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const newId = () => uuidV4();

const buildEmptyThread = (): ChatThread => ({
  id: newId(),
  title: '',
  messages: [],
  summary: undefined,
  updatedAt: Date.now(),
});

type ChatSessionApi = {
  hasActiveConversation: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  ensureActiveConversation: () => void;
  closeActiveConversation: () => void;
  send: (text: string) => Promise<void>;
  stopStreaming: () => void;
  resetToLanding: () => void;
};

export const useChatBot = ({
  userId,
  customerName,
}: {
  userId: number;
  customerName?: string;
}): ChatSessionApi => {
  // Storage key MUST be stable across UI2 + legacy.
  // Use the passed userId (login employee id).
  const storageKey = React.useMemo(() => {
    return userId ? `solairis.chat.${userId}` : null;
  }, [userId]);

  const [activeThreadId, setActiveThreadId] = React.useState<string | null>(
    null,
  );
  const [threads, setThreads] = React.useState<Record<string, ChatThread>>({});
  const [isStreaming, setIsStreaming] = React.useState(false);

  const abortRef = React.useRef<AbortController | null>(null);

  // Prevent "accidental" pointer wipes caused by remount/race.
  const explicitResetRef = React.useRef(false);

  // Track when we have loaded initial state for current key.
  const hydratedRef = React.useRef(false);

  // Load persisted state when key becomes available/changes.
  React.useEffect(() => {
    hydratedRef.current = false;

    if (!storageKey) {
      setActiveThreadId(null);
      setThreads({});
      return;
    }

    const persisted = safeJsonParse<PersistedState>(
      localStorage.getItem(storageKey),
    );

    if (!persisted) {
      setActiveThreadId(null);
      setThreads({});
      hydratedRef.current = true;
      return;
    }

    setActiveThreadId(persisted.activeThreadId ?? null);
    setThreads(persisted.threads ?? {});
    hydratedRef.current = true;
  }, [storageKey]);

  // Persist state (hydration-safe + protects activeThreadId unless explicitly reset)
  React.useEffect(() => {
    if (!storageKey) return;
    if (!hydratedRef.current) return;

    const prev = safeJsonParse<PersistedState>(
      localStorage.getItem(storageKey),
    );

    const next: PersistedState = { activeThreadId, threads };

    const isImplicitNull =
      next.activeThreadId === null &&
      Object.keys(next.threads).length > 0 &&
      !explicitResetRef.current;

    // If some remount tries to wipe the active thread pointer, keep the previous pointer.
    if (isImplicitNull && prev?.activeThreadId) {
      next.activeThreadId = prev.activeThreadId;
    }

    localStorage.setItem(storageKey, JSON.stringify(next));

    // Only applies to the next persist after reset.
    explicitResetRef.current = false;
  }, [storageKey, activeThreadId, threads]);

  const currentThread = React.useMemo(() => {
    if (!activeThreadId) return null;
    return threads[activeThreadId] ?? null;
  }, [activeThreadId, threads]);

  const messages = React.useMemo(
    () => currentThread?.messages ?? [],
    [currentThread],
  );
  const hasActiveConversation = Boolean(activeThreadId && currentThread);

  const stopStreaming = React.useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const resetToLanding = React.useCallback(() => {
    explicitResetRef.current = true;
    stopStreaming();
    setActiveThreadId(null);
  }, [stopStreaming]);

  const ensureActiveConversation = React.useCallback(() => {
    setActiveThreadId((prevActive) => {
      if (prevActive && threads[prevActive]) return prevActive;

      const t = buildEmptyThread();
      setThreads((prev) => ({ ...prev, [t.id]: t }));
      return t.id;
    });
  }, [threads]);

  const closeActiveConversation = React.useCallback(() => {
    explicitResetRef.current = true;
    stopStreaming();
    setActiveThreadId(null);
  }, [stopStreaming]);

  const send = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      let threadId = activeThreadId;

      if (!threadId || !threads[threadId]) {
        const t = buildEmptyThread();
        threadId = t.id;
        setThreads((prev) => ({ ...prev, [t.id]: t }));
        setActiveThreadId(t.id);
      }

      const userMsg: ChatMessage = {
        id: newId(),
        role: 'user',
        content: trimmed,
        createdAt: Date.now(),
      };

      const assistantId = newId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
      };

      setThreads((prev) => {
        const t = prev[threadId!] ?? buildEmptyThread();
        const nextTitle =
          t.title && t.title.trim().length > 0
            ? t.title
            : trimmed.length > 40
            ? `${trimmed.slice(0, 40)}…`
            : trimmed;

        return {
          ...prev,
          [threadId!]: {
            ...t,
            title: nextTitle,
            messages: [...t.messages, userMsg, assistantMsg],
            updatedAt: Date.now(),
          },
        };
      });

      stopStreaming();
      abortRef.current = new AbortController();
      setIsStreaming(true);

      try {
        const tNow = threads[threadId!] ?? buildEmptyThread();

        const chat_history = tNow.messages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role, content: m.content }))
          .concat([{ role: 'user' as const, content: trimmed }]);

        await streamChat({
          request: {
            user_id: userId,
            customer_name: customerName ?? '',
            query: trimmed,
            session_id: threadId!,
            chat_history,
            summary: tNow.summary ?? '',
            deployment: process.env.NODE_ENV ?? 'unknown',
          },
          signal: abortRef.current.signal,
          onDelta: (delta) => {
            setThreads((prev) => {
              const t = prev[threadId!]!;
              const msgs = t.messages.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + delta } : m,
              );

              return {
                ...prev,
                [threadId!]: { ...t, messages: msgs, updatedAt: Date.now() },
              };
            });
          },
          onMeta: (meta) => {
            setThreads((prev) => {
              const t = prev[threadId!]!;
              return {
                ...prev,
                [threadId!]: {
                  ...t,
                  summary: meta.summary ?? t.summary,
                  updatedAt: Date.now(),
                },
              };
            });
          },
        });
      } catch {
        setThreads((prev) => {
          const t = prev[threadId!]!;
          const msgs = t.messages.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content:
                    'Sorry — something went wrong while generating a reply.',
                }
              : m,
          );

          return {
            ...prev,
            [threadId!]: { ...t, messages: msgs, updatedAt: Date.now() },
          };
        });
      } finally {
        abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [activeThreadId, customerName, stopStreaming, threads, userId],
  );

  return {
    hasActiveConversation,
    messages,
    isStreaming,
    ensureActiveConversation,
    closeActiveConversation,
    send,
    stopStreaming,
    resetToLanding,
  };
};
